export interface AiDetectionMetric {
  expertID: string;
  name: string;
  email: string | null;
  detectionRuns: number;
  infoFlags: number;
  warningFlags: number;
  lastRunAt: Date | null;
}

export interface AiDetectionMetricRow extends AiDetectionMetric {
  /** Field-level warning flags divided by settled detection passes. */
  warningFlagsPerRun: number;
  /** 1-based position among experts with at least one warning flag. */
  warningRank: number | null;
  isTopWarningQuartile: boolean;
}

export interface AiDetectionTotals {
  detectionRuns: number;
  infoFlags: number;
  warningFlags: number;
}

export interface AiDetectionReport {
  rows: AiDetectionMetricRow[];
  totals: AiDetectionTotals;
  flaggedExpertCount: number;
  topWarningExpertCount: number;
  topWarningExpertIds: ReadonlySet<string>;
}

function safeCounter(value: number): number {
  return Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

function normalizedMetric(metric: AiDetectionMetric): AiDetectionMetric | null {
  const expertID = typeof metric.expertID === 'string' ? metric.expertID.trim() : '';
  if (!expertID) return null;

  const name = typeof metric.name === 'string' && metric.name.trim()
    ? metric.name.trim()
    : expertID;
  const email = typeof metric.email === 'string' && metric.email.trim()
    ? metric.email.trim()
    : null;

  return {
    expertID,
    name,
    email,
    detectionRuns: safeCounter(metric.detectionRuns),
    infoFlags: safeCounter(metric.infoFlags),
    warningFlags: safeCounter(metric.warningFlags),
    lastRunAt: metric.lastRunAt instanceof Date ? metric.lastRunAt : null,
  };
}

function compareWarningCounts(a: AiDetectionMetric, b: AiDetectionMetric): number {
  const byWarnings = b.warningFlags - a.warningFlags;
  if (byWarnings !== 0) return byWarnings;

  const aPerRun = a.detectionRuns > 0 ? a.warningFlags / a.detectionRuns : 0;
  const bPerRun = b.detectionRuns > 0 ? b.warningFlags / b.detectionRuns : 0;
  const byRate = bPerRun - aPerRun;
  if (byRate !== 0) return byRate;

  const byRuns = b.detectionRuns - a.detectionRuns;
  if (byRuns !== 0) return byRuns;

  const byName = a.name.localeCompare(b.name);
  return byName !== 0 ? byName : a.expertID.localeCompare(b.expertID);
}

/**
 * Build the admin-facing warning ranking from all aggregate expert records.
 * The top quartile is calculated among experts with at least one warning flag;
 * ties at the cutoff are included so equal warning counts receive the same
 * indicator. Warning counts are field-level, matching the server aggregate.
 */
export function buildAiDetectionReport(metrics: AiDetectionMetric[]): AiDetectionReport {
  const normalized = metrics
    .map(normalizedMetric)
    .filter((metric): metric is AiDetectionMetric => metric !== null);
  const sorted = [...normalized].sort(compareWarningCounts);
  const flagged = sorted.filter((metric) => metric.warningFlags > 0);
  const quartileSize = flagged.length > 0 ? Math.max(1, Math.ceil(flagged.length / 4)) : 0;
  const cutoff = quartileSize > 0 ? flagged[quartileSize - 1].warningFlags : 0;
  const topWarningExpertIds = new Set(
    flagged
      .filter((metric) => metric.warningFlags >= cutoff)
      .map((metric) => metric.expertID),
  );
  const warningRanks = new Map<string, number>();
  flagged.forEach((metric, index) => warningRanks.set(metric.expertID, index + 1));

  const rows = sorted.map<AiDetectionMetricRow>((metric) => ({
    ...metric,
    warningFlagsPerRun: metric.detectionRuns > 0
      ? metric.warningFlags / metric.detectionRuns
      : 0,
    warningRank: warningRanks.get(metric.expertID) ?? null,
    isTopWarningQuartile: topWarningExpertIds.has(metric.expertID),
  }));

  const totals = normalized.reduce<AiDetectionTotals>(
    (acc, metric) => ({
      detectionRuns: acc.detectionRuns + metric.detectionRuns,
      infoFlags: acc.infoFlags + metric.infoFlags,
      warningFlags: acc.warningFlags + metric.warningFlags,
    }),
    { detectionRuns: 0, infoFlags: 0, warningFlags: 0 },
  );

  return {
    rows,
    totals,
    flaggedExpertCount: flagged.length,
    topWarningExpertCount: topWarningExpertIds.size,
    topWarningExpertIds,
  };
}

export function isTopWarningExpert(
  expertID: string | null | undefined,
  report: AiDetectionReport | null | undefined,
): boolean {
  return !!expertID && !!report?.topWarningExpertIds.has(expertID);
}
